<template>
    <div class="view--presale-list">
        <b-row class="mb-4" v-if="!loadingPresales">
            <b-col>
                <b-card>
                    <b-card-title>Resultaten</b-card-title>

                    <div v-if="presales.length === 0">
                        <p>Geen resultaten gevonden.</p>
                    </div>

                    <b-table-simple v-else>
                        <b-thead>
                            <b-tr>
                                <b-th v-for="(displayName, index) in visibleColumns" :key="index">
                                    {{ displayName }}
                                </b-th>
                            </b-tr>
                        </b-thead>

                        <b-tbody>
                            <b-tr v-for="(presale, index) in sortedPresales" :key="index" @click="onRowClick(presale)">
                                <template v-for="(value, key, index) in presale">
                                    <b-td :key="index" v-if="!isHiddenColumn(key)">
                                        <template v-if="isDateColumn(key)">
                                            {{ parseDate(value) }}
                                        </template>

                                        <template v-else>
                                            {{ value }}
                                        </template>
                                    </b-td>
                                </template>
                            </b-tr>
                        </b-tbody>
                    </b-table-simple>
                </b-card>
            </b-col>
        </b-row>
    </div>
</template>

<style lang="scss">
.view--presale-list {
    table {
        tbody {
            tr {
                cursor: pointer;
                transition: background .1s ease-in-out;
                &:hover {
                    background-color: hsl(221, 100%, 96%);
                }
            }
        }
    }
}
</style>

<script>
import { cloneDeep } from 'lodash';
import { date } from "@/helpers/date.js";

export default {
    name: 'PresaleList',

    data() {
        return {
            loadingPresales: false,

            presales: [], // filled on mount.

            columnNames: {
                product_identifier:     'Artikelnummer',
                product_name:           'Artikelnaam',
                virtual_stock:          'Fictieve stock',
                sold_quantity:          'Aantal voorverkocht',
                cancelled_quantity:     'Aantal geannuleerd',
                presale_starts_at:      'Start presale',
                release_date:           'Release datum',

                id:                     'ID',
                additional_identifier:  'Extra identifier',
                created_at:             'Aangemaakt op',
                presale_ends_at:        'Presale loopt tot',
                updated_at:             'Bijgewerkt op',
            },

            hiddenColumns: [
                'id',
                'additional_identifier',
                'created_at',
                'presale_ends_at',
                'updated_at',
            ],

            dateColumns: [
                'created_at',
                'updated_at',
                'presale_starts_at',
                'presale_ends_at',
                'release_date',
            ],
        }
    },

    computed: {
        sortedPresales() {
            return this.presales.map(row => {
                const sortedRow = {};

                Object.keys(this.columnNames).forEach(columnName => {
                    sortedRow[columnName] = row[columnName];
                });

                Object.keys(row).forEach(columnName => {
                    if (!sortedRow[columnName]) {
                        sortedRow[columnName] = row[columnName];
                    }
                });
            return sortedRow;
            })
        },

        columns() {
            return this.sortedPresales.reduce((acc, presale) => {
                for (const key in presale) {
                    if (!acc.hasOwnProperty(key)) {
                        acc[key] = (key in this.columnNames) ? this.columnNames[key] : key
                    }
                }
                return acc
            }, {})
        },

        visibleColumns() {
            return Object.keys(this.columns)
                    .filter((key) => !this.hiddenColumns.includes(key))
                    .reduce((acc, key) => {
                        acc[key] = this.columns[key]
                        return acc
                    }, {})
        },

        isHiddenColumn() {
            return (key) => this.hiddenColumns.includes(key)
        },

        isDateColumn() {
            return (key) => this.dateColumns.includes(key)
        },
    },

    methods: {
        parseDate(val) {
            return val ? date(val) : null  // Prevents showing 1970-01-01
        },

        // fetchPresales fetches the presales with a given filter (passed via GET params)
        // and puts the result into this.presales.
        fetchPresales() {
            const params = {
                include: 'sold_quantity,cancelled_quantity',
                sort_by: 'release_date',
                sort_dir: 'asc',
            };

            const dateToday = new Date().toISOString().split('T')[0]; // YYYY-MM-DD

            switch (this.$route.name) {
                case 'PresaleListIncomplete':
                    params.presale_starts_at = 'null';
                    params.presale_starts_at_operator = '=';
                    params.presale_release_date = dateToday;
                    params.presale_release_date_operator = '>';
                    break;

                case 'PresaleListFuture':
                    params.presale_starts_at = dateToday;
                    params.presale_starts_at_operator = '>';
                    params.presale_ends_at = dateToday;
                    params.presale_ends_at_operator = '>';
                    break;

                case 'PresaleListActive':
                    params.presale_starts_at = dateToday;
                    params.presale_starts_at_operator = '<=';
                    params.presale_ends_at = dateToday;
                    params.presale_ends_at_operator = '>';
                    break;

                case 'PresaleListHistory':
                    params.presale_ends_at = dateToday;
                    params.presale_ends_at_operator = '<=';
                    break;
            }

            this.presales = {};
            this.loadingPresales = true;

            this.$http({
                url: `/api/rest/presale-schedules`,
                params
            })
            .then((response) => {
                this.presales = cloneDeep(response.data);
            })
            .catch((error) => {
                this.$alert(`Fout tijdens het ophalen van de presale gegevens: ${error}`, 'error');
            })
            .then(() => {
                this.loadingPresales = false;
            });
        },

        onRowClick (presale) {
            this.$router.push({
                name: 'PresaleDetail',
                params: {
                    presaleId: `${presale.id}`,
                },
            })
        },
    },

    mounted() {
        this.fetchPresales();
    },

    watch: {
        '$route': 'fetchPresales',
    },
    
}
</script>